/**
 * Depth-tile dataset edition (spec 10, gap 3). Fetched once per session from
 * `/depthtiles/meta`, cached in IndexedDB so a cold offline start still has an
 * edition string to stamp `?v=` with for cache-busting, and so downloaded
 * regions (`RegionMeta.depthEdition`) can be compared for staleness. Falls
 * back to the last-known edition when the network is unavailable.
 */
import type { DepthTilesMeta } from '@boatnav/schemas';
import { getDepthMeta, putDepthMeta } from '../../lib/db.js';

let cachedEdition: string | null = null;
let inFlight: Promise<string | null> | null = null;

async function fetchEdition(): Promise<string | null> {
  try {
    const res = await fetch('/depthtiles/meta');
    if (!res.ok) return null;
    const data = (await res.json()) as DepthTilesMeta;
    return typeof data.edition === 'string' && data.edition.length > 0 ? data.edition : null;
  } catch {
    return null;
  }
}

/** Resolve the current depth-tile edition: network first, IDB fallback. */
export async function loadDepthEdition(): Promise<string | null> {
  if (cachedEdition != null) return cachedEdition;
  if (inFlight) return inFlight;

  inFlight = (async (): Promise<string | null> => {
    const fetched = await fetchEdition();
    if (fetched != null) {
      cachedEdition = fetched;
      await putDepthMeta(fetched).catch(() => undefined);
      return fetched;
    }
    const stored = await getDepthMeta().catch(() => undefined);
    cachedEdition = stored?.edition ?? null;
    return cachedEdition;
  })();

  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}

/** Synchronous read of whatever edition has already been resolved this session. */
export function getCachedDepthEdition(): string | null {
  return cachedEdition;
}
